import { useState, useEffect, useMemo } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import {
  Typography,
  Box,
  Button,
  Autocomplete,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useLocation } from "react-router";

import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { IProject } from "../../types/project";
import { IConversation } from "../../types/twilio";
import StandardTable from "../tables/StandardTable";
import { convertAllDates } from "../../utils/function";
import { useAppDispatch } from "../../app/hooks";
import { handleModal } from "../../features/modal/modalSlice";
import { showErrorSnackbar, showSuccessSnackbar } from "../../features/snackbar/snackbarSlice";
import { GlobalModal } from "../../features/modal/Modal";
import Conversation from "../registrant/Conversation";

const Sms = () => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const location: any = useLocation();
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [conversation, setConversation] = useState<IConversation | null>(null);
  const [project, setProject] = useState<IProject | null>(null);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);

  // Text a Number
  const [showTextANumber, setShowTextANumber] = useState<boolean>(false);
  const [textProject, setTextProject] = useState<IProject | null>(null);
  const [textPhoneNumber, setTextPhoneNumber] = useState<string>("");
  const [textType, setTextType] = useState<string>("project");
  const [textMessage, setTextMessage] = useState<string>("");

  // Texts
  const [userType, setUserType] = useState<string>(location && location.state && location.state.user ? location.state.user : "All");

  const [getTextMessages, { loading }] = useLazyQuery(GETTEXTMESSAGES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setConversations([...data.getTextMessages.conversations].reverse());
      setNextPageToken(data.getTextMessages.nextPageToken || null);
    },
    onError: (err) => {
      console.log(err, "err");
    },
  });

  const [sendText, { loading: sendTextLoading }] = useMutation(SENDTEXT, {
    onCompleted: () => {
      storeDispatch(showSuccessSnackbar("Text Message Sent!"));
      setTextMessage("");
      storeDispatch(handleModal(false));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(err.message));
    },
  });

  useEffect(() => {
    if (project) {
      getTextMessages({
        variables: {
          project: project._id,
          pageToken,
        },
      });
    } else if (user?.type !== "Developer") {
      getTextMessages({
        variables: {
          user: user?._id,
          pageToken,
        },
      });
    }
    // eslint-disable-next-line
  }, [project, userType, pageToken]);

  const displayModal = (conversation: IConversation) => {
    setShowTextANumber(false);
    setConversation(conversation);
    storeDispatch(handleModal(true));
  };

  const displayTextANumber = () => {
    setConversation(null);
    setTextProject(project);
    setTextPhoneNumber("");
    setTextType("project");
    setTextMessage("");
    setShowTextANumber(true);
    storeDispatch(handleModal(true));
  };

  const handleSendText = () => {
    if (!textProject) {
      return storeDispatch(showErrorSnackbar("Please select a project"));
    }
    if (textPhoneNumber.length !== 10) {
      return storeDispatch(showErrorSnackbar("Please enter a valid 10 digit phone number"));
    }
    if (!textMessage.trim()) {
      return storeDispatch(showErrorSnackbar("Please enter a message"));
    }

    const existingConversation = conversations.find((c: any) => c.phone && c.phone.replace(/\D/g, "").endsWith(textPhoneNumber));

    sendText({
      variables: {
        project: textType === "project" ? textProject._id : null,
        user: user?._id,
        customerPhone: parseInt(textPhoneNumber, 10),
        message: textMessage,
        conversation: existingConversation ? existingConversation.conversationSid : undefined,
      },
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "Registrant",
        accessor: (rowData: any, index: number) => {
          if (rowData.registrant) {
            return (
              <Link
                target="_blank"
                style={{ textDecoration: "none", color: "#000" }}
                to={`/dashboard/project/${rowData.registrant.project._id}/registrant/${rowData.registrant._id}`}
              >
                <strong>{rowData.registrant.fullName}</strong>
              </Link>
            );
          } else {
            return rowData.name;
          }
        },
      },
      {
        Header: "Latest Message",
        accessor: (rowData: any, index: number) => {
          return rowData.messages[0].text;
        },
      },
      {
        Header: "Last Reply Date",
        accessor: (rowData: any, index: number) => {
          return convertAllDates(rowData.messages[0].dateCreated, "PPpp");
        },
      },
      {
        Header: "Latest Reply By",
        accessor: (rowData: any, index: number) => {
          if (rowData.messages[0].author.includes("+")) {
            if (rowData.registrant) {
              return rowData.registrant.fullName;
            }
          } else return rowData.messages[0].author;
        },
      },
      {
        Header: "View Conversation",
        accessor: (rowData: any, index: number) => {
          return (
            <Button variant="contained" color="primary" onClick={() => displayModal(rowData)}>
              View Conversation
            </Button>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, loading]);

  return (
    <Box>
      <GlobalModal>
        {conversation ? (
          <>
            <Typography variant="h2" component="div" gutterBottom>
              <strong>Conversation with {conversation.name}</strong>
            </Typography>
            <Conversation
              conversationDetails={conversation}
              messages={[...conversation.messages].reverse()}
              selectedUser={user}
              registrant={conversation.registrant}
              project={conversation.registrant ? conversation.registrant.project : conversation.project}
            />
          </>
        ) : showTextANumber ? (
          <>
            <Typography variant="h2" sx={{ mb: 2 }}>
              <strong>{textProject ? `Texting about ${textProject.name}` : "Text a Number"}</strong>
            </Typography>
            <Autocomplete
              options={[user?.projectAccess!.map((projectAccess: any) => projectAccess?.project)].flat()}
              freeSolo={false}
              value={textProject}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option: any) => option.name}
              onChange={(e, value) => {
                setTextProject(value);
              }}
              renderInput={(params) => <TextField {...params} label="Which project are you texting about?" required />}
            />
            <TextField
              sx={{ my: 2 }}
              title={"Phone Number"}
              name={"phoneNumber"}
              type="number"
              fullWidth
              required
              value={textPhoneNumber}
              label={"Phone Number"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTextPhoneNumber(e.target.value);
              }}
            />
              <FormControl>
                <FormLabel id="text-a-number-radio-buttons-group-label">Which number would you like to text with?</FormLabel>
                <RadioGroup
                  value={textType}
                  onChange={(e) => setTextType(e.target.value)}
                  aria-labelledby="text-a-number-radio-buttons-group-label"
                  defaultValue="project"
                  name="text-a-number-radio-buttons-group"
                >
                  <FormControlLabel value="project" control={<Radio />} label="Project Number" />
                  {user?.trackingPhone ? (
                    <FormControlLabel value="user" control={<Radio />} label="Personal Number" />
                  ) : null}
                </RadioGroup>
              </FormControl>
            <TextField
              sx={{ mb: 2 }}
              name={"message"}
              fullWidth
              required
              multiline
              minRows={3}
              value={textMessage}
              label={"Message"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTextMessage(e.target.value);
              }}
            />
            <Box>
              <Button variant="contained" color="success" disabled={sendTextLoading} onClick={handleSendText}>
                {sendTextLoading ? <CircularProgress size={16} color="inherit" /> : "Send"}
              </Button>
            </Box>
          </>
        ) : null}
      </GlobalModal>
      <Typography variant="h2" component="div" gutterBottom>
        <strong>SMS</strong>
      </Typography>
      <Box sx={{ width: "500px" }}>
        <Autocomplete
          options={[user?.projectAccess!.map((projectAccess: any) => projectAccess?.project)].flat()}
          freeSolo={false}
          value={project}
          isOptionEqualToValue={(option, value) => option === value}
          getOptionLabel={(option: any) => option.name}
          onChange={(e, value) => {
            setTokenHistory([]);
            setNextPageToken(null);
            setPageToken(undefined);
            setProject(value);
          }}
          renderInput={(params) => <TextField {...params} label="Projects" />}
        />
      </Box>
      <Button sx={{ mt: 2 }} color="success" variant="contained" onClick={displayTextANumber}>
        Text a Number
      </Button>
      <Box sx={{ mt: 2 }}>
        <StandardTable data={conversations} columns={columns} loading={loading} />
      </Box>
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Button
          disabled={tokenHistory.length === 0}
          onClick={() => {
            const newHistory = [...tokenHistory];
            const previousToken = newHistory.pop();
            setTokenHistory(newHistory);
            setPageToken(previousToken);
          }}
        >
          {"<"}
        </Button>
        <span>{tokenHistory.length + 1}</span>
        <Button
          disabled={!nextPageToken}
          onClick={() => {
            setTokenHistory((tokenHistory) => [...tokenHistory, pageToken]);
            setPageToken(nextPageToken || undefined);
          }}
        >
          {">"}
        </Button>
      </Box>
    </Box>
  );
};

const GETTEXTMESSAGES = gql`
  query getTextMessages($project: MongoID, $user: MongoID, $pageToken: String) {
    getTextMessages(project: $project, user: $user, pageToken: $pageToken) {
      nextPageToken
      conversations {
        conversationSid
        name
        state
        registrant {
          project {
            name
            _id
          }
          _id
          firstName
          lastName
          fullName
          realtorType
          textUnsubscribed {
            type
            date
          }
          connections {
            date
          }
        }
        user {
          fullName
          trackingPhone
        }
        project {
          _id
          name
          trackingPhone
        }
        phone
        phoneNumber
        messages {
          id
          author
          text
          dateCreated
          phone
          direction
        }
      }
    }
  }
`;

const SENDTEXT = gql`
  mutation sendText($project: MongoID, $user: MongoID, $customerPhone: Float, $message: String!, $conversation: String) {
    sendText(project: $project, user: $user, customerPhone: $customerPhone, message: $message, conversation: $conversation) {
      author
      text
      dateCreated
      phone
      direction
    }
  }
`;

export default Sms;
